// Scherm 14 — het Arbeidsmarktbeeld.
//
// Vorm uit W16 §6b, door de owner vastgesteld: WIDGETS BOVENAAN, ONTWIKKELINGEN
// ERONDER. De V1-dummy is hier niet het uitgangspunt (dit scherm bestond er
// niet), de UX wel — dezelfde kaarten, filterbalk, tabelkop van 34px en het
// detailpaneel dat van rechts inschuift als op de andere tabbladen.
//
// De pagina zet twee voedingen naast elkaar die elkaar niet vervangen:
// CIJFERREEKSEN meten hoeveel, RAPPORTEN verklaren waarom.
//
// FILTERBALK (owner-feedback 2026-08-24): alles in dropdowns, óók het gebied,
// en in dezelfde volgorde als Skills en Bronnen — eerst zoeken, dan sector, dan
// de rest. De gebiedspillen die hier eerst stonden zijn weg: één scherm dat zijn
// belangrijkste as anders bedient dan alle andere schermen kost de gebruiker
// meer dan het oplevert.
//
// Drie eerlijkheidsregels die zichtbaar blijven:
//  1. Elk cijfer draagt zijn landelijke referentielijn — anders is het geen
//     informatie maar een getal.
//  2. Elk voorbehoud staat ONDER het cijfer, niet in een voetnoot.
//  3. Wat niet gevuld kan worden staat er mét de reden (W8 §e).

import { useEffect, useMemo, useState } from "react";

import {
  BewegerBlok,
  KrapteBlok,
  OntbreektWidget,
  ReeksWidget,
  RegioAfwijkingBlok,
  VerouderdMelding,
  periodeLabel,
  waardeLabel,
} from "../components/ArbeidsmarktWidgets";
import { EmptyList, LoadError } from "../components/Empty";
import { FilterChips, type Chip } from "../components/FilterChips";
import { FilterMenu } from "../components/FilterMenu";
import { ObservatieDetail } from "../components/ObservatieDetail";
import { TrendDetail } from "../components/TrendDetail";
import { TrendVanBevinding } from "../components/TrendlaagOud";
import { trendlaagAan } from "../nieuw/trendlaag/schakelaar";
import {
  Card,
  FilterBar,
  PageHeader,
  Pager,
  PanelHeader,
  InfoIcon,
  SearchField,
  SectionLabel,
  SlidePanel,
  TableHead,
} from "../components/ui";
import type {
  ArbeidsmarktReeksResponse,
  ObservatieDetailResponse,
  TrendDetailResponse,
} from "../contracts";
import { api } from "../lib/api";
import { useMeta } from "../lib/meta";
import { useMij } from "../lib/mij";
import { useDebounced, useResource } from "../lib/useResource";
import { color, metric, size, weight } from "../tokens";

const COLS = "1fr 150px 104px 108px 132px";
// Reparatie 06-10: op 390 px duwde de tabel de pagina 430 px zijwaarts. Zelfde
// patroon als Trends en Bronnen: de TABEL schuift binnen de kaart, de pagina
// niet; de titelkolom houdt een leesbare breedte.
const TABEL_MIN = 760;
const LABELS = ["Vaststelling", "Gebied", "Richting", "Periode", "Bron"] as const;
const PAGE_SIZE = 25;

// Machinewaarde -> wat er op het scherm staat. Eén plek per as (backlog 30).
const THEMA_LABEL: Record<string, string> = {
  werkgelegenheid: "Werkgelegenheid",
  vraag: "Vraag naar personeel",
  aanbod: "Aanbod van personeel",
  beleid: "Beleid",
  lonen: "Lonen",
  technologie_in_werk: "Technologie in het werk",
  onduidelijk: "Thema onduidelijk",
};
const ONDERBOUWING_LABEL: Record<string, string> = {
  eigen_onderzoek: "Eigen onderzoek",
  cijfers_derden: "Cijfers van derden",
  bewering: "Bewering",
  casus_anekdote: "Casus of anekdote",
  onduidelijk: "Onduidelijk",
};
const RICHTING_LABEL: Record<string, string> = {
  stijgend: "Stijgend",
  dalend: "Dalend",
  stabiel: "Stabiel",
  onduidelijk: "Onbenoemd",
};
const SCHAAL_LABEL: Record<string, string> = {
  lokaal: "Lokaal — één plaats",
  regionaal: "Regionaal",
  nationaal: "Nationaal — Nederland",
  EU: "Europees",
  mondiaal: "Mondiaal",
  onduidelijk: "Onduidelijk",
};
// Trends dragen een eigen richtingwoordenlijst (opkomend/groeiend/afnemend),
// niet die van de waarnemingen — twee assen, twee lijsten (backlog 30).
const RICHTING_TREND: Record<string, string> = {
  opkomend: "Opkomend",
  groeiend: "Groeiend",
  afnemend: "Afnemend",
  stabiel: "Stabiel",
};
const INDICATOR_LABEL: Record<string, string> = {
  banen: "Banen",
  vacatures: "Vacatures",
  ww: "WW-uitkeringen",
  instroom: "Instroom onderwijs",
  lonen: "Lonen",
  spanning: "Spanningsindicator",
};
// Twee sectorwaarden staan niet in sectors.yaml: ze zeggen iets over de
// uitspraak, niet over een sector.
const SECTOR_EXTRA: Record<string, string> = {
  sectorbreed: "Sectorbreed — geldt voor alle sectoren",
  onduidelijk: "Sector onduidelijk",
};

type AsNaam = "sector" | "gebiedsniveau" | "thema" | "richting" | "onderbouwing" | "schaal" | "indicator";
type Assen = Record<AsNaam, string[]>;
const LEEG: Assen = {
  sector: [], gebiedsniveau: [], thema: [], richting: [],
  onderbouwing: [], schaal: [], indicator: [],
};

export function ArbeidsmarktPage() {
  const [assen, setAssen] = useState<Assen>(LEEG);
  const [zoekVeld, setZoekVeld] = useState("");
  const zoek = useDebounced(zoekVeld);
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState<string | null>(null);
  const [reeksSel, setReeksSel] = useState<{ bron: string; indicator: string } | null>(null);
  const [trendSel, setTrendSel] = useState<string | null>(null);
  const laagAan = trendlaagAan();

  const meta = useMeta();
  // Bouwrun baan C (C4): de eigen propositie gaat mee, zodat "Krapte per
  // propositie" die vooraan zet. Het overzicht wacht NIET op /mij (de pagina
  // laadt even snel als vroeger); alleen wie een propositie heeft, krijgt één
  // tweede ronde met die propositie erbij.
  const mij = useMij();
  const propositie = mij.stand === "ingelogd" ? mij.mij.propositie ?? null : null;
  const overzicht = useResource(() => api.arbeidsmarkt(propositie), [propositie]);

  const params = useMemo(() => {
    const p: Record<string, unknown> = { page, pageSize: PAGE_SIZE };
    for (const [as, waarden] of Object.entries(assen)) {
      if (waarden.length) p[as] = waarden;
    }
    if (zoek) p.zoek = zoek;
    return p;
  }, [assen, page, zoek]);
  const lijst = useResource(() => api.observaties(params), [params]);

  const detail = useResource<ObservatieDetailResponse | null>(
    () => (sel ? api.observatie(sel) : Promise.resolve(null)),
    [sel],
    sel !== null,
  );
  // Een sterke trend is geen samenvatting maar een volwaardige trend, met eigen
  // narratief, bewijs en vaardigheden. Doorklikken opent hetzelfde paneel als op
  // de Trends-tab; zonder die doorklik zou dit scherm de sterkste trends van het
  // corpus tonen als een doodlopend kaartje.
  const trend = useResource<TrendDetailResponse | null>(
    () => (trendSel ? api.trend(trendSel) : Promise.resolve(null)),
    [trendSel],
    // Met de trendlaag aan toont TrendVanBevinding de trend; het oude detail niet vooraf ophalen.
    trendSel !== null && !laagAan,
  );
  const reeks = useResource<ArbeidsmarktReeksResponse | null>(
    () => (reeksSel ? api.reeks(reeksSel.bron, reeksSel.indicator) : Promise.resolve(null)),
    [reeksSel],
    reeksSel !== null,
  );

  // Paneel schuift in met een animatie; de selectie mag pas daarna weg.
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (sel) {
      const t = setTimeout(() => setOpen(true), metric.panelOpenDelay);
      return () => clearTimeout(t);
    }
    setOpen(false);
  }, [sel]);

  // ── de assen: machinewaarde <-> label, op één plek ────────────────────────
  const sectorLabels = useMemo(() => {
    const uit: Record<string, string> = { ...SECTOR_EXTRA };
    for (const s of meta?.sectors ?? []) uit[s.key] = s.label;
    return uit;
  }, [meta]);

  const facets = lijst.data?.facets ?? {};
  const gebiedBakken = lijst.data?.counts ?? overzicht.data?.gebieden ?? [];

  // De sectoras is de LIJST, niet wat er toevallig in beeld is. Alle andere
  // schermen tonen alle zestien sectoren uit config/sectors.yaml; een scherm dat
  // er stilzwijgend vier weglaat omdat er nu geen waarneming bij hoort, laat de
  // gebruiker denken dat die sector niet bestaat (owner-feedback 2026-08-25).
  // "Sectorbreed" en "onduidelijk" staan er apart achteraan: dat zijn geen
  // sectoren maar uitspraken over de reikwijdte.
  const sectorVolgorde = useMemo(() => {
    const uitMeta = (meta?.sectors ?? []).map((s) => s.key);
    return [...uitMeta, ...Object.keys(SECTOR_EXTRA)];
  }, [meta]);

  function as(naam: AsNaam, labels: Record<string, string>) {
    const facetTelling = new Map((facets[naam] ?? []).map((f) => [f.value, f.count]));
    // Het gebied komt niet uit de facetten maar uit `counts`: die telt ALTIJD
    // alle bakken, ook de bak waar je niet op filtert (telregel W1c §7).
    const waarden =
      naam === "gebiedsniveau"
        ? gebiedBakken.map((b) => ({ value: b.id, count: b.count, label: b.label }))
        : naam === "sector" && sectorVolgorde.length
          ? sectorVolgorde.map((key) => ({
              value: key,
              count: facetTelling.get(key) ?? 0,
              label: labels[key] ?? key,
            }))
          : (facets[naam] ?? []).map((f) => ({ value: f.value, count: f.count, label: labels[f.value] ?? f.value }));
    const terug: Record<string, string> = {};
    const counts: Record<string, number> = {};
    const disabled: Record<string, string> = {};
    for (const w of waarden) {
      terug[w.label] = w.value;
      counts[w.label] = w.count;
      if (w.count === 0) {
        // Zichtbaar maar niet aanklikbaar, mét reden — een filter dat gegarandeerd
        // nul oplevert is geen keuze.
        disabled[w.label] = "Geen waarneming in deze selectie.";
      }
    }
    const heen = (v: string) => waarden.find((w) => w.value === v)?.label ?? labels[v] ?? v;
    return {
      options: waarden.map((w) => w.label),
      values: assen[naam].map(heen),
      counts,
      disabled,
      onChange: (gekozen: string[]) => {
        setAssen((a) => ({ ...a, [naam]: gekozen.map((l) => terug[l]!).filter(Boolean) }));
        setPage(1);
      },
      label: heen,
    };
  }

  const ASSEN: { naam: AsNaam; titel: string; labels: Record<string, string>; note?: string }[] = [
    { naam: "sector", titel: "Sector", labels: sectorLabels,
      note: "Alle zestien sectoren uit de configuratie, ook die zonder waarneming in deze selectie — dezelfde lijst als op Skills, Trends en Bronnen. 'Sectorbreed' en 'onduidelijk' staan onderaan: dat zijn geen sectoren maar uitspraken over de reikwijdte." },
    { naam: "gebiedsniveau", titel: "Gebied", labels: {},
      note: "Speelt dit hier? Onze regio is de arbeidsmarktregio Zuidoost-Brabant plus Helmond-De Peel; Noord-Brabant is de ruimere provincielaag. De bakken sluiten elkaar uit, dus de getallen tellen op tot het totaal." },
    { naam: "thema", titel: "Thema", labels: THEMA_LABEL,
      note: "Waar de waarneming over gaat." },
    { naam: "richting", titel: "Richting", labels: RICHTING_LABEL,
      note: "Welke kant het op beweegt volgens de bron. 'Onbenoemd' is een meting: veel uitspraken beschrijven een stand zonder beweging." },
    { naam: "onderbouwing", titel: "Onderbouwing", labels: ONDERBOUWING_LABEL,
      note: "Waar de uitspraak op steunt. Eigen onderzoek weegt zwaarder dan een bewering zonder cijfers — dat telt ook mee in de rangschikking." },
    { naam: "schaal", titel: "Schaal", labels: SCHAAL_LABEL,
      note: "Hoe breed de bron zijn uitspraak maakt. Een andere as dan Gebied: die zegt waaróver het gaat, deze hoe ver het reikt." },
    { naam: "indicator", titel: "Reeks", labels: INDICATOR_LABEL,
      note: "Waarnemingen die aan een cijferreeks hangen; die tonen de grafiek in het detailpaneel." },
  ];

  const chips: Chip[] = [
    ...(zoek ? [{ axis: "zoek", value: zoek, label: `“${zoek}”` }] : []),
    ...ASSEN.flatMap(({ naam, labels }) =>
      assen[naam].map((v) => ({
        axis: naam,
        value: v,
        label: naam === "gebiedsniveau"
          ? gebiedBakken.find((b) => b.id === v)?.label ?? v
          : labels[v] ?? v,
      })),
    ),
  ];

  const totaal = lijst.data?.total ?? 0;
  const paginas = Math.max(1, Math.ceil(totaal / PAGE_SIZE));
  const gefilterd = chips.length > 0;

  return (
    <>
      <PageHeader
        title="Arbeidsmarkt"
        count={
          overzicht.data ? (
            <>
              {totaal.toLocaleString("nl-NL")} waarnemingen in beeld
              {gefilterd ? ` van ${overzicht.data.totaal.toLocaleString("nl-NL")}` : ""}
            </>
          ) : (
            "laden…"
          )
        }
      />

      {overzicht.error && <LoadError reason={overzicht.error} onRetry={overzicht.reload} />}

      {/* ── de cijfers ──────────────────────────────────────────────────── */}
      {overzicht.data && (
        <>
          <div style={{ height: 22 }} />
          <SectionLabel>Wat de cijfers meten</SectionLabel>
          <VerouderdMelding status={overzicht.data.bronstatus} />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(232px,1fr))",
              gap: 14,
              marginTop: 10,
            }}
          >
            {overzicht.data.reeksen.map((k) => (
              <ReeksWidget
                key={`${k.bron}-${k.indicator}`}
                kaart={k}
                onOpen={() => setReeksSel({ bron: k.bron, indicator: k.indicator })}
              />
            ))}
            {overzicht.data.ontbreekt.map((b) => (
              <OntbreektWidget key={b.id} blok={b} />
            ))}
          </div>

          {/* Bouwrun baan C (C4): drie blokken onder de bestaande widgets. */}
          {(overzicht.data.krapte || overzicht.data.regioAfwijking !== undefined) && (
            <div
              data-testid="arbeidsmarkt-blokken"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill,minmax(min(100%,300px),1fr))",
                gap: 14,
                marginTop: 14,
              }}
            >
              <KrapteBlok blok={overzicht.data.krapte} />
              <RegioAfwijkingBlok blok={overzicht.data.regioAfwijking} />
              <BewegerBlok blok={overzicht.data.grootsteBeweger} />
            </div>
          )}

          {/* De vijf sterkst onderbouwde trends. Elk met een i'tje dat zegt
              WAAROM hij sterk heet — die redenen komen uit de uitlichtrubriek,
              het scherm verzint er niets bij. */}
          {(overzicht.data.sterksteTrends ?? []).length > 0 && (
            <>
              <div style={{ height: 30 }} />
              <SectionLabel>
                {/* Kopje en i'tje op één lijn: 11px hoofdletters naast een
                    18px icoon lopen anders zichtbaar uit elkaar. */}
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                Wat de rapporten verklaren
                <InfoIcon
                  tooltip={
                    "De vijf arbeidsmarkttrends met de sterkste onderbouwing, op volgorde van " +
                    "de uitlichtrubriek: bronzwaarte, claimkracht binnen de bron, hoe breed " +
                    "hij gezien is en hoe vers het bewijs is. De cijfers hierboven meten wát " +
                    "er gebeurt; deze trends verklaren waarom."
                  }
                  maxWidth={340}
                />
                </span>
              </SectionLabel>
              <div
                style={{
                  display: "grid",
                  // 196 en niet 232: bij 232 vragen vijf kaarten 1208px terwijl er
                  // 1204 beschikbaar is, en dan valt de vijfde alleen op een
                  // tweede regel. Vier pixels, hele rij kapot.
                  gridTemplateColumns: "repeat(auto-fit,minmax(196px,1fr))",
                  gap: 14,
                  marginTop: 10,
                }}
              >
                {(overzicht.data.sterksteTrends ?? []).map((tr) => (
                  <Card key={tr.trendId} style={{ padding: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        padding: "14px 16px 0",
                      }}
                    >
                      <span
                        style={{
                          fontSize: size.label,
                          fontWeight: weight.medium,
                          color: color.faint,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                      >
                        {RICHTING_TREND[tr.richting ?? ""] ?? "Trend"}
                      </span>
                      <InfoIcon
                        tooltip={
                          <>
                            <b>Waarom deze trend sterk heet</b>
                            <ul style={{ margin: "6px 0 0", paddingLeft: 16 }}>
                              {tr.redenen.map((r) => (
                                <li key={r}>{r}</li>
                              ))}
                            </ul>
                            {!laagAan && tr.uitlichtscore !== null && tr.uitlichtscore !== undefined && (
                              <div style={{ marginTop: 6 }}>
                                Uitlichtscore {tr.uitlichtscore.toLocaleString("nl-NL")} van 10.
                              </div>
                            )}
                          </>
                        }
                        maxWidth={320}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setTrendSel(tr.trendId)}
                      title="Open de volledige trend"
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: "none",
                        font: "inherit",
                        cursor: "pointer",
                        padding: "8px 16px 14px",
                      }}
                    >
                      <span
                        style={{
                          display: "block",
                          fontSize: size.body,
                          fontWeight: weight.medium,
                          color: color.ink,
                          lineHeight: 1.45,
                        }}
                      >
                        {tr.titel}
                      </span>
                      <span
                        style={{
                          display: "block",
                          fontSize: 11.5,
                          color: color.faint,
                          marginTop: 12,
                          borderTop: `1px solid ${color.lineSoft}`,
                          paddingTop: 8,
                        }}
                      >
                        {tr.bronnen} {tr.bronnen === 1 ? "bron" : "bronnen"}
                        {tr.redenen.some((r) => r.startsWith("raakt "))
                          ? ` · ${tr.redenen.find((r) => r.startsWith("raakt "))}`
                          : ""}
                      </span>
                    </button>
                  </Card>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* ── de ontwikkelingen ───────────────────────────────────────────── */}
      <div style={{ height: 30 }} />
      <SectionLabel>Wat de rapporten waarnemen</SectionLabel>

      <FilterBar
        style={{ marginTop: 10 }}
        chips={
          <FilterChips
            chips={chips}
            onRemove={(chip) => {
              if (chip.axis === "zoek") setZoekVeld("");
              else
                setAssen((a) => ({
                  ...a,
                  [chip.axis]: a[chip.axis as AsNaam].filter((v) => v !== chip.value),
                }));
              setPage(1);
            }}
            onClearAll={() => {
              setAssen(LEEG);
              setZoekVeld("");
              setPage(1);
            }}
          />
        }
      >
          {/* Zoeken vóór sector — dezelfde opening als Skills, Trends en Bronnen. */}
          <SearchField
            value={zoekVeld}
            onChange={(v) => {
              setZoekVeld(v);
              setPage(1);
            }}
            placeholder="Zoek waarnemingen…"
          />
          <div style={{ width: 1, height: 24, background: color.border, flexShrink: 0 }} />
          {ASSEN.map(({ naam, titel, labels, note }) => {
            const a = as(naam, labels);
            return (
              <FilterMenu
                key={naam}
                label={titel}
                note={note}
                wide
                options={a.options}
                values={a.values}
                counts={a.counts}
                disabled={a.disabled}
                onChange={a.onChange}
              />
            );
          })}
      </FilterBar>

      {lijst.error ? (
        <LoadError reason={lijst.error} onRetry={lijst.reload} />
      ) : (
        <Card style={{ marginTop: 12, padding: 0, opacity: lijst.stale ? 0.6 : 1 }}>
          <div data-testid="am-tabel-schuif" style={{ overflowX: "auto" }}>
          <div style={{ minWidth: TABEL_MIN }}>
          <TableHead cols={COLS} labels={LABELS} />
          {(lijst.data?.items ?? []).map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setSel(o.id)}
              style={{
                display: "grid",
                gridTemplateColumns: COLS,
                gap: 12,
                width: "100%",
                textAlign: "left",
                padding: "11px 16px",
                border: "none",
                borderTop: `1px solid ${color.lineFaint}`,
                background: "transparent",
                cursor: "pointer",
                font: "inherit",
                alignItems: "start",
              }}
            >
              <span style={{ fontSize: size.body, color: color.ink, lineHeight: 1.45 }}>
                {o.naam}
                {o.cijfer && (
                  <span style={{ color: color.soft, fontWeight: weight.medium }}> — {o.cijfer}</span>
                )}
              </span>
              <span style={{ fontSize: size.meta, color: color.soft }}>
                {o.gebied ?? <span style={{ color: color.faint }}>niet benoemd</span>}
              </span>
              <span style={{ fontSize: size.meta, color: color.soft }}>
                {RICHTING_LABEL[o.richting] ?? o.richting}
              </span>
              <span style={{ fontSize: size.meta, color: color.soft }}>
                {o.peildatum ?? <span style={{ color: color.faint }}>—</span>}
              </span>
              <span style={{ fontSize: size.meta, color: color.soft }}>
                {o.bron.uitgever}
                {/* Methode v3: het gezagscijfer achter de uitgever vervalt. */}
                {!meta?.bronlabels && o.bron.gezag !== null && o.bron.gezag !== undefined && (
                  <span style={{ color: color.faint }}> · {o.bron.gezag}</span>
                )}
              </span>
            </button>
          ))}
          {lijst.data && lijst.data.items.length === 0 && (
            <EmptyList
              reason="Geen waarneming voldoet aan deze combinatie van filters."
              onReset={() => {
                setAssen(LEEG);
                setZoekVeld("");
                setPage(1);
              }}
            />
          )}
          </div>
          </div>
        </Card>
      )}

      {totaal > PAGE_SIZE && <Pager page={page} totalPages={paginas} onPage={setPage} />}

      {/* ── het bewijs ──────────────────────────────────────────────────── */}
      <SlidePanel open={open && !!detail.data} onClose={() => setSel(null)} terugLabel="Terug naar het overzicht">
        {detail.data && (
          <ObservatieDetail
            detail={detail.data}
            onClose={() => setSel(null)}
            onReeks={(bron, indicator) => setReeksSel({ bron, indicator })}
          />
        )}
      </SlidePanel>

      <SlidePanel open={!!trendSel && (laagAan || !!trend.data)} onClose={() => setTrendSel(null)} terugLabel="Terug naar het overzicht">
        {laagAan ? (
          trendSel && <TrendVanBevinding id={trendSel} onClose={() => setTrendSel(null)} />
        ) : (
          trend.data && <TrendDetail trend={trend.data.trend} onClose={() => setTrendSel(null)} />
        )}
      </SlidePanel>

      <SlidePanel open={!!reeksSel && !!reeks.data} onClose={() => setReeksSel(null)} level={2} terugLabel="Terug naar de waarneming">
        {reeks.data && <ReeksPaneel data={reeks.data} onClose={() => setReeksSel(null)} />}
      </SlidePanel>
    </>
  );
}

/** De hele reeks achter één cijfer — elke regio met zijn laatste stand. */
function ReeksPaneel({
  data,
  onClose,
}: {
  data: ArbeidsmarktReeksResponse;
  onClose: () => void;
}) {
  const soort = data.reeks.periodeSoort;
  return (
    <>
      <PanelHeader onClose={onClose} />
      <div style={{ padding: 24 }}>
        <h2
          style={{
            fontSize: size.panelTitle,
            fontWeight: weight.bold,
            color: color.ink,
            margin: 0,
          }}
        >
          {data.reeks.titel}
        </h2>
        <p style={{ fontSize: size.meta, color: color.soft, marginTop: 6 }}>
          {data.reeks.bron} · {data.reeks.eenheid} · per {soort}
          {data.reeks.voorbehoud ? ` · ${data.reeks.voorbehoud}` : ""}
        </p>

        <SectionLabel>Per regio</SectionLabel>
        <div style={{ marginTop: 8 }}>
          {data.regios.map((r) => {
            const laatste = r.punten[r.punten.length - 1];
            const eerste = r.punten[0];
            return (
              <div
                key={r.code}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 12,
                  padding: "8px 0",
                  borderTop: `1px solid ${color.lineFaint}`,
                }}
              >
                <div style={{ fontSize: size.body, color: color.body }}>
                  {r.naam}
                  {r.referentie && (
                    <span style={{ color: color.faint, fontSize: size.meta }}> · referentie</span>
                  )}
                  {eerste && (
                    <span style={{ fontSize: size.meta, color: color.faint }}>
                      {" "}
                      — vanaf {periodeLabel(eerste.periode, soort)}, {r.punten.length} metingen
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: size.body,
                    fontWeight: weight.medium,
                    color: color.ink,
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  {laatste ? waardeLabel(laatste.waarde, data.reeks.eenheid) : "—"}
                  <span style={{ fontSize: size.meta, color: color.faint, fontWeight: weight.regular }}>
                    {" "}
                    {laatste ? periodeLabel(laatste.periode, soort) : ""}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
